import { defineStore, storeToRefs } from 'pinia'
import type { AxiosError, AxiosResponse } from 'axios'
import { useQuery, useMutation } from '@tanstack/vue-query'
import { computed, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import api from '@dashboard/services/axiosApi'
import * as jsondiffpatch from 'jsondiffpatch'
import { klona } from 'klona'
import type { Team } from '@dashboard/types/team'
import type { OrganizationRef, ProjectAccess } from '@dashboard/types/organization'
import { changedTeamPaths, pathsOverlap, shouldAutosaveTeamChange } from './teamAutosaveSync'
import type { AutosaveStatus } from '@dashboard/modules/settings/composables/useAutosave'
import { requestUuid } from '@dashboard/services/uuid'
import { useNotificationStore } from './notificationStore'
import { useAuthStore } from './authStore'
import { useConfigStore } from './configStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { ROUTES } from '@dashboard/router/routeNames'

export type TeamCreateInput = Pick<Team, 'name' | 'website'> & { organization_pid?: string }
export interface TeamRequestError {
  message?: string
  code?: string
  reason?: string
  errors?: Record<string, string[]>
}
export type TeamCreateAfterCreate = 'dashboard' | 'onboarding'

export interface TeamCreateWarning {
  code: string
  message: string
}

export type TeamCreateBillingModel = 'legacy' | 'per_team'

export interface TeamCreateResult {
  team: Team
  warning: TeamCreateWarning | null
  replayed: boolean
  requiresBillingSetup: boolean
  billingModel: TeamCreateBillingModel
  hasPaymentMethod: boolean
}

export interface TeamCreateOptions {
  afterCreate?: TeamCreateAfterCreate
  clientRequestId?: string
}

type TeamCreateResponse = {
  team: Team
  warning?: TeamCreateWarning | null
  replayed?: boolean
  requires_billing_setup?: boolean
  billing_model?: TeamCreateBillingModel
  has_payment_method?: boolean
}
export type TeamListItem = Pick<Team, 'id' | 'name' | 'website' | 'personal_team'> & {
  pid: Team['pid'] | 'demo'
  is_client_team?: boolean
  is_demo?: boolean
  /** The organization that owns the project; absent for demo teams. */
  organization?: OrganizationRef | null
  access?: ProjectAccess | null
}

/** One team PUT; `seq` numbers saves in send order. */
type TeamSave = { team: Team; omitName?: boolean; paths: string[]; seq: number }

export const useTeamStore = defineStore('team', () => {
  const doesOwnCurrentTeam = ref(false) as Ref<boolean>
  const authStore = useAuthStore()
  const configStore = useConfigStore()
  const notificationStore = useNotificationStore()
  const { currentTeam, currentTeamPid, fullResponse, config, teamMembers, snapshots, isApplyingRemoteTeam } =
    storeToRefs(configStore)
  const router = useRouter()
  const { user } = storeToRefs(authStore)

  // Detect if we're in demo mode
  const isDemo = computed(() => currentTeamPid.value === 'demo')

  // Query for authenticated user's real teams - NEVER affected by demo mode
  // This prevents race conditions during registration where FakeLayout sets demo mode
  // but we need to fetch the user's actual teams for post-login redirect
  const userTeamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamListItem[]>('/api/teams').then((res) => res.data),
    enabled: computed(() => !!user.value)
  })
  const demoTeamsQuery = useQuery({
    queryKey: ['demo-teams'],
    queryFn: () => api.get<TeamListItem[]>('/api/demo/teams').then((res) => res.data),
    enabled: computed(() => isDemo.value && !user.value)
  })

  // Return real teams for authenticated users, demo teams only for unauthenticated demo viewers
  const teams = computed(() => {
    if (user.value) {
      return userTeamsQuery.data.value || []
    }
    return demoTeamsQuery.data.value || []
  })

  const rejectedName = ref<{ pid: Team['pid']; name: string; message: string } | null>(null)
  const nameError = computed(() =>
    rejectedName.value?.pid === currentTeam.value?.pid && rejectedName.value?.name === currentTeam.value?.name
      ? rejectedName.value?.message
      : undefined
  )
  // Team fields edited, sent, saved and failed, so each settings section can
  // show the save state of only the fields it owns (useTeamAutosave). Saves
  // are numbered in send order and each PUTs the whole team, so a field
  // follows the newest save carrying its latest edit, whatever order the
  // responses land in.
  const pendingSavePaths = ref(new Set<string>())
  const sendingSaveCounts = ref(new Map<string, number>())
  const failedSavePaths = ref(new Set<string>())
  const savedPaths = ref(new Set<string>())
  const lastSentIn = new Map<string, number>()
  let saveCount = 0
  let firstSaveForProject = 1

  // Sync, so no render or Retry in between sees the previous project's state.
  watch(
    () => currentTeam.value?.pid,
    () => {
      pendingSavePaths.value.clear()
      sendingSaveCounts.value.clear()
      failedSavePaths.value.clear()
      savedPaths.value.clear()
      lastSentIn.clear()
      firstSaveForProject = saveCount + 1
    },
    { flush: 'sync' }
  )

  // A deep watcher hands back the same object for in-place edits, so diff
  // against our own copy of the last value to learn which fields changed.
  let teamSnapshot: Team | null = currentTeam.value ? klona(currentTeam.value) : null
  watch(
    () => currentTeam.value,
    (is) => {
      const previous = teamSnapshot
      teamSnapshot = is ? klona(is) : null
      if (!is) {
        return
      }

      const diff = jsondiffpatch.diff(previous, is) as Record<string, unknown> | null | undefined
      if (
        !diff ||
        !shouldAutosaveTeamChange({
          diff,
          isApplyingRemoteTeam: isApplyingRemoteTeam.value
        })
      ) {
        return
      }

      for (const path of changedTeamPaths(diff)) {
        pendingSavePaths.value.add(path)
        failedSavePaths.value.delete(path)
        savedPaths.value.delete(path)
      }
      void debouncedUpdateTeam(is)
    },
    { deep: true }
  )

  const settleSave = (save: TeamSave, succeeded: boolean) => {
    // A save queued or sent before a project switch says nothing about the project open now.
    if (save.team.pid !== currentTeam.value?.pid || save.seq < firstSaveForProject) {
      return
    }
    for (const path of save.paths) {
      const count = (sendingSaveCounts.value.get(path) ?? 0) - 1
      if (count > 0) {
        sendingSaveCounts.value.set(path, count)
      } else {
        sendingSaveCounts.value.delete(path)
      }
    }
    if (succeeded) {
      // The whole team went out, so this also saves fields an earlier save failed on.
      for (const [path, sentIn] of lastSentIn) {
        if (sentIn > save.seq || pendingSavePaths.value.has(path)) {
          continue
        }
        // A rejected project name rides along unsent until it is corrected.
        if (save.omitName && path === 'name') {
          failedSavePaths.value.add(path)
          continue
        }
        failedSavePaths.value.delete(path)
        savedPaths.value.add(path)
      }
      return
    }
    for (const path of save.paths) {
      // A newer save decides the fields it carries, and one that already saved them wins.
      const isLatest = lastSentIn.get(path) === save.seq && !pendingSavePaths.value.has(path)
      if (isLatest && !savedPaths.value.has(path)) {
        failedSavePaths.value.add(path)
      }
    }
  }

  const updateTeamMutation = useMutation<AxiosResponse, AxiosError<TeamRequestError>, TeamSave>({
    retry: false,
    mutationFn: ({ team, omitName }) => {
      const { name, ...rest } = team
      return api.put(`/api/${team.pid}/team`, omitName ? rest : { ...rest, name })
    },
    onSuccess: (_, variables) => {
      if (
        !variables.omitName &&
        rejectedName.value?.pid === variables.team.pid &&
        rejectedName.value.name === variables.team.name
      ) {
        rejectedName.value = null
      }
      settleSave(variables, true)
      // Track team settings update
      void recordAnalyticsEvent(AnalyticsEventTypes.SETTINGS_UPDATED, {
        settingType: 'team',
        teamId: variables.team.pid,
        teamName: variables.team.name,
        updatedFields: Object.keys(variables.team)
      })
    },
    onError: (error, variables) => {
      settleSave(variables, false)
      const message = error.response?.data.errors?.name?.[0]
      if (error.response?.status === 422 && message) {
        rejectedName.value = { pid: variables.team.pid, name: variables.team.name, message }
      } else {
        notificationStore.addError(error.response?.data.message || 'We couldn’t save your project settings.')
      }
    }
  })

  const saveTeamNow = (team: Team) => {
    // Keep saving other settings while a rejected project name is still in the field.
    const omitName = rejectedName.value?.pid === team.pid && rejectedName.value.name === team.name
    const seq = ++saveCount
    const paths = [...pendingSavePaths.value]
    pendingSavePaths.value.clear()
    for (const path of paths) {
      sendingSaveCounts.value.set(path, (sendingSaveCounts.value.get(path) ?? 0) + 1)
      lastSentIn.set(path, seq)
    }
    updateTeamMutation.mutate({ team: { ...team }, omitName, paths, seq })
  }

  /** Save state of the team fields at `paths` (dot paths; a parent covers its children). */
  const teamSaveStatus = (paths: string[]): AutosaveStatus => {
    const touches = (changed: Iterable<string>) =>
      [...changed].some((field) => paths.some((path) => pathsOverlap(field, path)))
    if (touches(pendingSavePaths.value) || touches(sendingSaveCounts.value.keys())) {
      return 'saving'
    }
    if (touches(failedSavePaths.value)) {
      return 'error'
    }
    return touches(savedPaths.value) ? 'saved' : 'idle'
  }

  /** Resend every team field whose last save failed. */
  const retryTeamSave = () => {
    if (!currentTeam.value || failedSavePaths.value.size === 0) {
      return
    }
    for (const path of failedSavePaths.value) {
      pendingSavePaths.value.add(path)
    }
    failedSavePaths.value.clear()
    saveTeamNow(currentTeam.value)
  }

  const deleteTeamMutation = useMutation({
    mutationFn: (variables: { team: Pick<Team, 'pid' | 'name'> }) => api.delete(`/api/teams/${variables.team.pid}`),
    onSuccess: (_, variables) => {
      // The deleted project's config is gone; callers move to another project.
      void userTeamsQuery.refetch()
      authStore.getUser(true).catch((error: unknown) => {
        console.error('Failed to refresh user after team deletion:', error)
      })

      // Track team deletion
      void recordAnalyticsEvent(AnalyticsEventTypes.TEAM_DELETED, {
        teamId: variables.team.pid,
        teamName: variables.team.name
      })
    }
  })

  const removeTeamMemberMutation = useMutation({
    mutationFn: (variables: { memberId: number; isInvited: boolean }) =>
      api.delete(`/api/${currentTeamPid.value}/team/members/${variables.memberId}`, {
        params: {
          invited: variables.isInvited ? '1' : '0'
        }
      }),
    onSuccess: (_, variables) => {
      void configStore.configQuery.refetch()

      // Track team member removal
      void recordAnalyticsEvent(AnalyticsEventTypes.TEAM_MEMBER_REMOVED, {
        teamId: currentTeamPid.value,
        memberId: variables.memberId,
        wasInvited: variables.isInvited
      })
    }
  })

  const addTeamMemberMutation = useMutation<
    AxiosResponse,
    AxiosError<TeamRequestError>,
    { email: string; role: string }
  >({
    mutationFn: (memberData: { email: string; role: string }) =>
      api.post(`/api/${currentTeamPid.value}/team/members`, memberData),
    onSuccess: (_, variables) => {
      void configStore.configQuery.refetch()

      // Track team member addition
      void recordAnalyticsEvent(AnalyticsEventTypes.TEAM_MEMBER_ADDED, {
        teamId: currentTeamPid.value,
        memberEmail: variables.email,
        memberRole: variables.role
      })
    }
  })

  /** Resolves once the project is deleted; rejects with the server's error (409 `live_subscription`). */
  const deleteTeam = (team: Pick<Team, 'pid' | 'name'>) => deleteTeamMutation.mutateAsync({ team })
  const removeTeamMember = () => {
    const { isError, error, isPending, mutate, mutateAsync } = removeTeamMemberMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }
  const addTeamMember = () => {
    const { isError, error, isPending, mutate, mutateAsync } = addTeamMemberMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  const debouncedUpdateTeam = useDebounceFn(saveTeamNow, 2000)

  const create = async (team: TeamCreateInput, options: TeamCreateOptions = {}): Promise<TeamCreateResult> => {
    const clientRequestId = options.clientRequestId ?? requestUuid()
    const { data } = await api.post<TeamCreateResponse>('/api/teams', {
      ...team,
      client_request_id: clientRequestId
    })

    await userTeamsQuery.refetch()

    // Track team creation before we transition to the new dashboard.
    void recordAnalyticsEvent(AnalyticsEventTypes.TEAM_CREATED, {
      teamId: data.team.pid,
      teamName: team.name,
      teamWebsite: team.website
    })

    // Seed the config store with the newly-created team immediately so the
    // dashboard/onboarding UI can render the new team's
    // `ai_context_status=pending|failed|ready` state without waiting for the
    // `/configs/current` fetch to land. Also clear team-scoped data from the
    // previously-open project so we don't momentarily render stale config,
    // members, or snapshots while the new team's queries hydrate.
    //
    // Guard this whole-object seed from the team deep-watch autosave: the
    // create response intentionally returns a thin shell (often
    // `ai_context=null`) that the background enrichment job / config refetch
    // fills in moments later. Posting this shell back would clobber the
    // richer server state we are waiting for.
    configStore.runWithoutTeamAutosave(() => {
      currentTeamPid.value = data.team.pid
      currentTeam.value = data.team
      fullResponse.value = null
      config.value = null
      teamMembers.value = []
      snapshots.value = []
    })

    const destination = options.afterCreate === 'dashboard' ? ROUTES.DASHBOARD : ROUTES.ONBOARDING

    await router.push({
      name: destination,
      params: { teamPid: data.team.pid },
      query: data.requires_billing_setup ? { billingSetup: '1' } : undefined
    })

    if (data.warning?.message) {
      notificationStore.addError(data.warning.message, false)
    }

    return {
      team: data.team,
      warning: data.warning ?? null,
      replayed: data.replayed === true,
      requiresBillingSetup: data.requires_billing_setup === true,
      billingModel: data.billing_model ?? 'per_team',
      hasPaymentMethod: data.has_payment_method === true
    }
  }

  // Compute isSuccess based on which query is active
  const isSuccess = computed(() => {
    if (user.value) {
      return userTeamsQuery.isSuccess.value
    }
    return demoTeamsQuery.isSuccess.value
  })

  const isError = computed(() => {
    if (user.value) {
      return userTeamsQuery.isError.value
    }
    return demoTeamsQuery.isError.value
  })

  return {
    teams,
    currentTeam,
    nameError,
    doesOwnCurrentTeam,
    isDemo,
    isSuccess,
    isError,
    deleteTeam,
    create,
    removeTeamMember,
    addTeamMember,
    teamSaveStatus,
    retryTeamSave
  }
})
